import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { PendingPaymentFilters } from '../components/PendingPaymentFilters';
import { TransactionDetailsPanel } from '../components/TransactionDetailsPanel';
import { Pagination } from '../components/Pagination';
import { LoadingScreen } from '../components/LoadingScreen';
import { PendingApprovalsTab } from '../components/PendingApprovalsTab';
import { ApprovalAPI } from '../services/api';
import { PendingSale, ApprovalRequest, RejectRequest } from '../types/payment';
import { useAuth } from '../context/AuthContext';
import { hasPermission, getRoleDisplayName } from '../utils/rolePermissions';
import { useForm } from 'react-hook-form';
import { AlertService } from '../services/AlertService';
import { usePendingPaymentFilters } from '../hooks/usePendingPaymentFilters';

interface ApprovalFormData {
  amount?: number;
}

interface RejectFormData {
  reason: string;
}


export const Approvals: React.FC = () => {
  const { user } = useAuth();

  // The Preauth Sales (Direct Payment) tab was removed; the page always shows
  // the unified Links & Self-Auth approvals queue now. activeTab is kept only
  // so the (unreachable) preauth code below still compiles.
  const initialParams = new URLSearchParams(window.location.search);
  const [activeTab] = useState<'preauth' | 'payment_links'>('payment_links');
  const linkHighlightId = initialParams.get('highlight');

  const [pendingSales, setPendingSales] = useState<PendingSale[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedSale, setSelectedSale] = useState<PendingSale | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showEncryptedData, setShowEncryptedData] = useState<string | null>(null);
  const [decryptedData, setDecryptedData] = useState<any>(null);
  const [sortField, setSortField] = useState<keyof PendingSale>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [selectedTransactionForDetails, setSelectedTransactionForDetails] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const approvalForm = useForm<ApprovalFormData>();
  const rejectForm = useForm<RejectFormData>({
    defaultValues: { reason: '' }
  });

  const canApprove = !user?.view_only && hasPermission(user?.role, 'canApproveSales');
  const canDecrypt = hasPermission(user?.role, 'canDecryptCardData');

  // Initialize filters
  const {
    filters,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    filteredSales,
    filterStats
  } = usePendingPaymentFilters(pendingSales);

  useEffect(() => {
    if (canApprove) {
      loadPendingSales();
    }
  }, [canApprove]);

  const loadPendingSales = async () => {
    try {
      setLoading(true);
      const sales = await ApprovalAPI.getPendingSales(0, 100000);
      setPendingSales(sales);
    } catch (error: any) {
      console.error('Failed to load pending sales:', error);
      AlertService.error('Failed to load pending sales');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (saleId: string, data: ApprovalFormData) => {
    try {
      setProcessingId(saleId);
      const result = await ApprovalAPI.approveSale(saleId, data);
      
      if (result.response_code === '100') {
        AlertService.success('Payment approved and captured successfully');
        setShowApprovalModal(false);
        setSelectedSale(null);
        loadPendingSales();
      } else {
        AlertService.error(`Approval failed: ${result.responsetext}`);
      }
    } catch (error: any) {
      console.error('Approval failed:', error);
      AlertService.error(error.message || 'Approval failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (saleId: string, data: RejectFormData) => {
    try {
      setProcessingId(saleId);
      await ApprovalAPI.rejectSale(saleId, data);
      AlertService.success('Payment rejected successfully');
      setShowRejectModal(false);
      setSelectedSale(null);
      loadPendingSales();
    } catch (error: any) {
      console.error('Rejection failed:', error);
      AlertService.error(error.message || 'Rejection failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewEncryptedData = async (saleId: string, decrypt = false, reason = '') => {
    try {
      const data = await ApprovalAPI.getPendingSaleEncryptedData(saleId, decrypt, reason);
      setShowEncryptedData(saleId);
      if (decrypt) {
        setDecryptedData(data);
      }
      console.log('Encrypted data:', data);
      // You could show this in a modal or separate component
    } catch (error: any) {
      console.error('Failed to get encrypted data:', error);
      AlertService.error(error.message || 'Failed to get encrypted data');
    }
  };



  const handleManualAuth = async (saleId: string) => {
    // Manual authorization - directly approve without capture
    try {
      setProcessingId(saleId);
      const result = await ApprovalAPI.approveSale(saleId, {});
      
      if (result.response_code === '100') {
        AlertService.success('Payment manually authorized and captured successfully');
        loadPendingSales();
      } else {
        AlertService.error(`Manual authorization failed: ${result.responsetext}`);
      }
    } catch (error: any) {
      console.error('Manual authorization failed:', error);
      AlertService.error(error.message || 'Manual authorization failed');
    } finally {
      setProcessingId(null);
    }
  };

  const handleViewDetails = (sale: PendingSale) => {
    // Convert PendingSale to Transaction format for the universal details panel
    const transactionData = {
      ...sale,
      tx_type: 'preauth',
      nmi_response_code: sale.preauth_response_code,
      nmi_response_text: sale.preauth_response_text,
      nmi_transactionid: sale.preauth_transactionid,
      status: sale.status || 'pending',
      created_by_name: sale.created_by_name || sale.created_by,
      // Include approval/rejection data if available
      approved_by: sale.approved_by,
      approved_by_name: sale.approved_by_name,
      approved_at: sale.approved_at,
      rejected_by: sale.rejected_by,
      rejected_by_name: sale.rejected_by_name,
      rejected_at: sale.rejected_at,
      rejection_reason: sale.rejection_reason
    };
    
    setSelectedTransactionForDetails(transactionData);
    setShowDetailsPanel(true);
    // Clear any previous decrypted data
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleCloseDetails = () => {
    setShowDetailsPanel(false);
    setSelectedTransactionForDetails(null);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  // Pending-approval rows (links / self-auth) arrive already adapted to the
  // panel's transaction shape from PendingApprovalsTab.
  const handleViewPendingDetails = (tx: any) => {
    setSelectedTransactionForDetails(tx);
    setShowDetailsPanel(true);
    setDecryptedData(null);
    setShowEncryptedData(null);
  };

  const handleDecryptInPanel = async (saleId: string, reason: string) => {
    await handleViewEncryptedData(saleId, true, reason);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const openApprovalModal = (sale: PendingSale) => {
    setSelectedSale(sale);
    approvalForm.reset({ amount: sale.amount });
    setShowApprovalModal(true);
  };

  const openRejectModal = (sale: PendingSale) => {
    setSelectedSale(sale);
    rejectForm.reset({ reason: '' });
    setShowRejectModal(true);
  };


  const handleSort = (field: keyof PendingSale) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedAndPaginatedSales = React.useMemo(() => {
    const sorted = [...filteredSales].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      let comparison = 0;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        comparison = aVal.localeCompare(bVal);
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sorted.slice(startIndex, endIndex);
  }, [filteredSales, sortField, sortDirection, currentPage, itemsPerPage]);


  if (!canApprove) {
    return (
      <DashboardLayout>
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access approvals.</p>
          <p>Contact your administrator if you believe this is an error.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="approvals">
        <div className="page-header">
          <h1>Pending Approvals</h1>
          <p>Review and approve pending payment transactions</p>
          {activeTab === 'preauth' && (
            <button className="btn btn-outline-primary" onClick={loadPendingSales} disabled={loading}>
              {loading ? 'Loading...' : '🔄 Refresh'}
            </button>
          )}
        </div>

        {/* Direct-Payment "Preauth Sales" approvals were removed from the UI;
            the page now shows only the unified Links & Self-Auth queue. */}
        <PendingApprovalsTab highlightId={linkHighlightId} onViewDetails={handleViewPendingDetails} />

        {activeTab === 'preauth' && (<>
        {/* Filters */}
        {!loading && pendingSales.length > 0 && (
          <PendingPaymentFilters
            filters={filters}
            updateFilter={updateFilter}
            clearFilters={clearFilters}
            hasActiveFilters={hasActiveFilters}
            pendingSales={pendingSales}
            filterStats={filterStats}
          />
        )}

        <LoadingScreen 
          isVisible={loading} 
          message="Loading Pending Approvals..."
        />
        
        {!loading && (filteredSales.length === 0 && pendingSales.length === 0 ? (
          <div className="empty-state">
            <h3>No Pending Approvals</h3>
            <p>All payment transactions have been processed.</p>
          </div>
        ) : filteredSales.length === 0 ? (
          <div className="empty-state">
            <h3>No Matching Results</h3>
            <p>No pending payments match your current filter criteria.</p>
            <p>Try adjusting your filters or clearing them to see all payments.</p>
          </div>
        ) : (
          <div className="payments-table-container">
            <div className="table-wrapper">
              <table className="payments-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('created_by')} className="sortable">
                      Sale Created By
                      {sortField === 'created_by' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    <th onClick={() => handleSort('category')} className="sortable">
                      Sale Type
                      {sortField === 'category' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    <th onClick={() => handleSort('sale_state')} className="sortable">
                      Sale State
                      {sortField === 'sale_state' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    <th onClick={() => handleSort('amount')} className="sortable">
                      Amount
                      {sortField === 'amount' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    <th onClick={() => handleSort('created_at')} className="sortable">
                      Date & Time
                      {sortField === 'created_at' && (
                        <span className="sort-indicator">
                          {sortDirection === 'asc' ? ' ↑' : ' ↓'}
                        </span>
                      )}
                    </th>
                    <th className="actions-column">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedAndPaginatedSales.map(sale => (
                    <tr key={sale.id}>
                      <td className="created-by-cell">
                        {sale.created_by_name || sale.created_by || 'Unknown'}
                      </td>
                      <td className="sale-type-cell">
                        <span className="sale-type-badge">
                          {sale.category || 'N/A'}
                        </span>
                      </td>
                      <td className="sale-state-cell">
                        <span className="sale-state-badge">
                          {sale.sale_state || 'N/A'}
                        </span>
                      </td>
                      <td className="amount-cell">
                        <span className="amount-value">
                          ${Number(sale.amount).toFixed(2)}
                        </span>
                        {(sale as any).three_ds_status === 'Y' && (
                          <span className="badge-3ds">3DS</span>
                        )}
                        {(sale as any).three_ds_status === 'Y-3RI' && (
                          <span className="badge-3ds">3RI</span>
                        )}
                      </td>
                      <td className="date-cell">
                        {new Date(sale.created_at).toLocaleDateString()}<br/>
                        <small>{new Date(sale.created_at).toLocaleTimeString()}</small>
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          <button
                            className="btn btn-success btn-sm"
                            onClick={() => openApprovalModal(sale)}
                            disabled={processingId === sale.id}
                            title="Approve & Capture"
                          >
                            {processingId === sale.id ? '⏳' : '✅'} Approve
                          </button>
                          
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleManualAuth(sale.id)}
                            disabled={processingId === sale.id}
                            title="Manual Authorize"
                          >
                            {processingId === sale.id ? '⏳' : '🔐'} Auth
                          </button>
                          
                          
                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => handleViewDetails(sale)}
                            title="View Details"
                          >
                            📋 Details
                          </button>
                          
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => openRejectModal(sale)}
                            disabled={processingId === sale.id}
                            title="Reject Payment"
                          >
                            🚫 Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalItems={filteredSales.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              pageSizeOptions={[10, 20, 30, 50, 100]}
            />
          </div>
        ))}
        </>)}

        {/* Approval Modal */}
        {showApprovalModal && selectedSale && (
          <div className="modal-overlay" onClick={() => setShowApprovalModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <h2>Approve Payment</h2>
              <div className="approval-summary">
                <p><strong>Transaction:</strong> #{selectedSale.id.slice(-8)}</p>
                <p><strong>Customer:</strong> {selectedSale.first_name} {selectedSale.last_name}</p>
                <p><strong>Original Amount:</strong> ${selectedSale.amount.toFixed(2)}</p>
              </div>

              <form onSubmit={approvalForm.handleSubmit(data => handleApprove(selectedSale.id, data))}>
                <div className="form-group">
                  <label>Capture Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max={selectedSale.amount}
                    {...approvalForm.register('amount', {
                      valueAsNumber: true,
                      min: { value: 0.01, message: 'Amount must be greater than 0' },
                      max: { value: selectedSale.amount, message: 'Amount cannot exceed original amount' }
                    })}
                  />
                  <small>Leave empty to capture full amount</small>
                  {approvalForm.formState.errors.amount && (
                    <span className="error">{approvalForm.formState.errors.amount.message}</span>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowApprovalModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success" disabled={processingId === selectedSale.id}>
                    {processingId === selectedSale.id ? 'Processing...' : 'Approve & Capture'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && selectedSale && (
          <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <h2>Reject Payment</h2>
              <div className="rejection-summary">
                <p><strong>Transaction:</strong> #{selectedSale.id.slice(-8)}</p>
                <p><strong>Customer:</strong> {selectedSale.first_name} {selectedSale.last_name}</p>
                <p><strong>Amount:</strong> ${Number(selectedSale.amount).toFixed(2)}</p>
              </div>

              <form onSubmit={rejectForm.handleSubmit(data => handleReject(selectedSale.id, data))}>
                <div className="form-group">
                  <label>Rejection Reason*</label>
                  <textarea
                    rows={4}
                    {...rejectForm.register('reason', {
                      required: 'Rejection reason is required'
                    })}
                    placeholder="Please provide a reason for rejecting this payment..."
                  />
                  {rejectForm.formState.errors.reason && (
                    <span className="error">{rejectForm.formState.errors.reason.message}</span>
                  )}
                </div>

                <div className="form-actions">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowRejectModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-danger" disabled={processingId === selectedSale.id}>
                    {processingId === selectedSale.id ? 'Processing...' : 'Reject Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}


        {/* Transaction Details Panel */}
        <TransactionDetailsPanel
          transaction={selectedTransactionForDetails}
          isOpen={showDetailsPanel}
          onClose={handleCloseDetails}
          decryptedData={showEncryptedData === selectedTransactionForDetails?.id ? decryptedData : null}
          onDecryptData={handleDecryptInPanel}
        />
      </div>

      <style>{`
        .approvals {
          max-width: 100%;
          margin: 0;
          padding: 1rem;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-header div {
          flex: 1;
        }

        .page-header h1 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 2rem;
          font-weight: 600;
        }

        .page-header p {
          margin: 0;
          color: var(--text-secondary);
        }

        .approval-tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .approval-tab {
          padding: 0.6rem 1.1rem;
          background: transparent;
          border: none;
          border-bottom: 2px solid transparent;
          color: var(--text-secondary);
          font-size: 0.9rem;
          font-weight: 500;
          cursor: pointer;
          transition: color 0.2s, border-color 0.2s;
        }

        .approval-tab:hover {
          color: var(--text-primary);
        }

        .approval-tab.active {
          color: var(--primary);
          border-bottom-color: var(--primary);
        }

        .payments-table-container {
          background: var(--bg-card);
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
          overflow: hidden;
        }

        .table-wrapper {
          overflow-x: auto;
        }

        .payments-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.875rem;
        }

        .payments-table th,
        .payments-table td {
          padding: 0.75rem 1rem;
          text-align: left;
          border-bottom: 1px solid var(--border-primary);
          vertical-align: middle;
        }

        .payments-table th {
          background: var(--bg-secondary);
          font-weight: 600;
          color: var(--text-primary);
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .payments-table th.sortable {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.2s;
        }

        .payments-table th.sortable:hover {
          background: var(--bg-tertiary);
        }

        .sort-indicator {
          font-size: 0.75rem;
          margin-left: 0.5rem;
          color: var(--primary);
        }

        .payments-table tbody tr {
          transition: background-color 0.2s;
        }

        .payments-table tbody tr:hover {
          background: var(--bg-secondary);
        }

        .created-by-cell {
          font-weight: 500;
          color: var(--text-primary);
          min-width: 120px;
        }

        .sale-type-badge,
        .sale-state-badge {
          padding: 0.25rem 0.5rem;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
          background: var(--bg-tertiary);
          color: var(--text-primary);
          white-space: nowrap;
        }

        .amount-cell {
          font-weight: 600;
          color: var(--success);
          text-align: right;
          min-width: 100px;
        }


        .date-cell {
          min-width: 120px;
          color: var(--text-secondary);
        }

        .date-cell small {
          color: var(--text-tertiary);
          font-size: 0.75rem;
        }

        .actions-cell {
          min-width: 300px;
        }

        .action-buttons {
          display: flex;
          gap: 0.25rem;
          flex-wrap: wrap;
          align-items: center;
        }


        .loading {
          text-align: center;
          padding: 4rem 2rem;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--border-primary);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 1rem;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--bg-card);
          border-radius: 8px;
        }

        .empty-state h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
        }

        .empty-state p {
          margin: 0;
          color: var(--text-secondary);
        }

        .approvals-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 1.5rem;
        }

        .approval-card {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          border: 1px solid var(--border-primary);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid var(--border-primary);
        }

        .card-title h4 {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-size: 1.125rem;
        }

        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 500;
          text-transform: uppercase;
        }

        .status-badge.pending {
          background: var(--warning-light);
          color: var(--warning);
        }

        .card-amount {
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--success);
        }

        .card-details {
          margin-bottom: 1.5rem;
        }

        .detail-item {
          margin-bottom: 0.5rem;
          color: var(--text-secondary);
        }

        .detail-item strong {
          color: var(--text-primary);
        }

        .card-actions {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .card-actions button {
          flex: 1;
          min-width: 80px;
        }

        .btn-sm {
          padding: 0.375rem 0.75rem;
          font-size: 0.875rem;
          flex: none !important;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .modal {
          background: var(--bg-card);
          border-radius: 8px;
          padding: 2rem;
          width: 90%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .modal h2 {
          margin: 0 0 1.5rem 0;
          color: var(--text-primary);
        }

        .approval-summary,
        .rejection-summary {
          background: var(--bg-secondary);
          padding: 1rem;
          border-radius: 4px;
          margin-bottom: 1.5rem;
        }

        .approval-summary p,
        .rejection-summary p {
          margin: 0 0 0.5rem 0;
          color: var(--text-secondary);
        }

        .approval-summary p:last-child,
        .rejection-summary p:last-child {
          margin-bottom: 0;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .form-group input,
        .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          resize: vertical;
        }

        .form-group input:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary);
        }

        .form-group small {
          display: block;
          margin-top: 0.25rem;
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .error {
          display: block;
          color: var(--error);
          font-size: 0.875rem;
          margin-top: 0.25rem;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          margin-top: 2rem;
        }

        .access-denied {
          text-align: center;
          padding: 4rem 2rem;
          background: var(--bg-card);
          border-radius: 8px;
        }

        .access-denied h2 {
          color: var(--text-primary);
          margin-bottom: 1rem;
        }

        .access-denied p {
          color: var(--text-secondary);
          margin-bottom: 0.5rem;
        }

        @media (max-width: 768px) {
          .approvals-grid {
            grid-template-columns: 1fr;
          }

          .page-header {
            flex-direction: column;
            align-items: stretch;
          }

          .card-actions {
            flex-direction: column;
          }

          .card-actions button {
            flex: none;
          }
        }

        .badge-3ds {
          display: inline-block;
          padding: 0.125rem 0.4rem;
          border-radius: 4px;
          font-size: 0.65rem;
          font-weight: 700;
          background: rgba(16, 185, 129, 0.15);
          color: #059669;
          border: 1px solid rgba(16, 185, 129, 0.3);
          margin-left: 0.25rem;
          vertical-align: middle;
        }
      `}</style>
    </DashboardLayout>
  );
};